import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../AuthContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { LEARNING_URL, isLearningTarget } from '../../../utils/learningUrl';

/**
 * TwinGP partner SSO callback.
 * URL: /auth/partner/twingp?source=twingp&token=...&redirect_page=internship|learning|courses
 */
const PartnerAuthPage = () => {
  const [searchParams] = useSearchParams();
  const { setSessionFromPartner } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const source = searchParams.get('source') || '';
      const token = searchParams.get('token') || '';
      const redirectPage = searchParams.get('redirect_page') || 'internship';

      if (!token) {
        setError('Missing partner token. Please return to TwinGP and try again.');
        return;
      }

      try {
        const response = await authAPI.partnerTwingpAuth({
          source,
          token,
          redirect_page: redirectPage,
        });
        if (cancelled) return;

        const { access_token, redirect_to } = response.data;
        await setSessionFromPartner(access_token);

        const page = String(redirectPage).toLowerCase();
        const wantsCourses = page === 'learning' || page === 'courses';
        const target = redirect_to || (
          wantsCourses ? LEARNING_URL : '/internship/projects'
        );

        // External learning app is served outside the SPA router
        window.location.replace(isLearningTarget(target) ? LEARNING_URL : target);
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : 'Partner sign-in failed. Please return to TwinGP and try again.'
        );
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Intentionally once per URL (searchParams identity changes with query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (error) {
    return (
      <div style={{ maxWidth: 420, margin: '4rem auto', padding: '0 1.25rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.35rem', marginBottom: '0.75rem' }}>Sign-in failed</h1>
        <p style={{ color: '#555', marginBottom: '1.5rem' }}>{error}</p>
        <Link to="/login" style={{ color: '#0b5fff' }}>Go to CodeHub login</Link>
      </div>
    );
  }

  return <LoadingSpinner size="md" label="Signing you in…" fullPage />;
};

export default PartnerAuthPage;
